import React, { useState } from 'react';
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EngagementMetricsChart = ({ dateRange, userType }) => {
  const [viewMode, setViewMode] = useState('activity'); // activity or features

  const mockActivityData = [
    { day: 'Lun', sessions: 420, avgDuration: 18, activeUsers: 380 },
    { day: 'Mar', sessions: 450, avgDuration: 20, activeUsers: 410 },
    { day: 'Mer', sessions: 480, avgDuration: 22, activeUsers: 440 },
    { day: 'Jeu', sessions: 460, avgDuration: 21, activeUsers: 425 },
    { day: 'Ven', sessions: 440, avgDuration: 19, activeUsers: 400 },
    { day: 'Sam', sessions: 280, avgDuration: 15, activeUsers: 250 },
    { day: 'Dim', sessions: 290, avgDuration: 16, activeUsers: 260 }
  ];

  const mockFeatureUsageData = [
    { name: 'Quiz', value: 35, color: '#2563EB' },
    { name: 'Chat IA', value: 25, color: '#10B981' },
    { name: 'Exploration', value: 20, color: '#F59E0B' },
    { name: 'Résultats', value: 12, color: '#8B5CF6' },
    { name: 'Annuaire', value: 8, color: '#EC4899' }
  ];

  const mockRetentionData = [
    { period: 'Jour 1', rate: 100 },
    { period: 'Jour 7', rate: 68 },
    { period: 'Jour 14', rate: 52 },
    { period: 'Jour 30', rate: 45 },
    { period: 'Jour 60', rate: 38 },
    { period: 'Jour 90', rate: 35 }
  ];

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        style={{ fontSize: '12px', fontWeight: 'bold' }}
      >
        {`${(percent * 100)?.toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-sm">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Icon name="Activity" size={24} className="text-primary" />
          <div>
            <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
              Métriques d'Engagement
            </h2>
            <p className="text-xs md:text-sm text-muted-foreground mt-1">
              Activité utilisateur, durée des sessions et utilisation des fonctionnalités
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'activity' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('activity')}
            iconName="BarChart3"
          >
            Activité
          </Button>
          <Button
            variant={viewMode === 'features' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('features')}
            iconName="PieChart"
          >
            Fonctionnalités
          </Button>
        </div>
      </div>

      {viewMode === 'activity' ? (
        <div className="space-y-6">
          {/* Activity and Session Duration Chart */}
          <div className="h-80 md:h-96">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={mockActivityData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="day"
                  stroke="#6B7280"
                  style={{ fontSize: '12px' }}
                />
                <YAxis
                  yAxisId="left"
                  stroke="#6B7280"
                  style={{ fontSize: '12px' }}
                  label={{ value: 'Sessions', angle: -90, position: 'insideLeft', style: { fontSize: '12px', fill: '#6B7280' } }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="#6B7280"
                  style={{ fontSize: '12px' }}
                  label={{ value: 'Durée (min)', angle: 90, position: 'insideRight', style: { fontSize: '12px', fill: '#6B7280' } }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  formatter={(value, name) => {
                    const labels = {
                      sessions: ['Sessions', value],
                      avgDuration: ['Durée Moyenne', `${value} min`],
                      activeUsers: ['Utilisateurs Actifs', value]
                    };
                    return labels?.[name] || [name, value];
                  }}
                />
                <Legend
                  wrapperStyle={{ fontSize: '12px' }}
                  formatter={(value) => {
                    const labels = {
                      sessions: 'Sessions',
                      avgDuration: 'Durée Moyenne (min)',
                      activeUsers: 'Utilisateurs Actifs'
                    };
                    return labels?.[value] || value;
                  }}
                />
                <Bar yAxisId="left" dataKey="sessions" fill="#2563EB" radius={[8, 8, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="avgDuration" stroke="#10B981" strokeWidth={3} dot={{ fill: '#10B981', r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Retention Rate */}
          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Users" size={20} className="text-primary" />
              Taux de Rétention
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={mockRetentionData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis
                    dataKey="period"
                    stroke="#6B7280"
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis
                    stroke="#6B7280"
                    style={{ fontSize: '12px' }}
                    domain={[0, 100]}
                    label={{ value: 'Rétention (%)', angle: -90, position: 'insideLeft', style: { fontSize: '12px', fill: '#6B7280' } }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                    formatter={(value) => [`${value}%`, 'Rétention']}
                  />
                  <Line
                    type="monotone"
                    dataKey="rate"
                    stroke="#8B5CF6"
                    strokeWidth={3}
                    dot={{ fill: '#8B5CF6', r: 5 }}
                    activeDot={{ r: 7 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Feature Usage Pie Chart */}
          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-4 text-center">
              Utilisation des Fonctionnalités
            </h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={mockFeatureUsageData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {mockFeatureUsageData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry?.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                    formatter={(value) => [`${value}%`, 'Utilisation']}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Feature Usage List */}
          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">
              Détails par Fonctionnalité
            </h3>
            <div className="space-y-3">
              {mockFeatureUsageData?.map((feature, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: feature?.color }}
                    />
                    <span className="text-sm md:text-base font-medium text-foreground">
                      {feature?.name}
                    </span>
                  </div>
                  <span className="text-lg font-bold" style={{ color: feature?.color }}>
                    {feature?.value}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Engagement Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
        <div className="text-center">
          <p className="text-2xl md:text-3xl font-bold text-primary mb-1">8.2/10</p>
          <p className="text-xs md:text-sm text-muted-foreground">Score Engagement</p>
        </div>
        <div className="text-center">
          <p className="text-2xl md:text-3xl font-bold text-success mb-1">19.5 min</p>
          <p className="text-xs md:text-sm text-muted-foreground">Durée Moyenne</p>
        </div>
        <div className="text-center">
          <p className="text-2xl md:text-3xl font-bold text-foreground mb-1">35%</p>
          <p className="text-xs md:text-sm text-muted-foreground">Rétention 90j</p>
        </div>
        <div className="text-center">
          <p className="text-2xl md:text-3xl font-bold text-amber-500 mb-1">2,847</p>
          <p className="text-xs md:text-sm text-muted-foreground">Utilisateurs Actifs</p>
        </div>
      </div>
    </div>
  );
};

export default EngagementMetricsChart;